import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";

const WeekView = ({ currentDate, slots, onSlotClick, onCreateSlot, mode }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [dragEnd, setDragEnd] = useState(null);
  const gridRef = useRef(null);

  const getWeekDays = (date) => {
    const days = [];
    const start = new Date(date);
    start.setDate(start.getDate() - start.getDay()); // Start of week (Sunday)
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatTime = (hour) => {
    return `${String(hour).padStart(2, "0")}:00`;
  };

  const getSlotsForDateTime = (dateStr, hour) => {
    return slots.filter((slot) => {
      if (slot.date !== dateStr) return false;
      const startHour = parseInt(slot.start_time.split(":")[0]);
      const endHour = parseInt(slot.end_time.split(":")[0]);
      return hour >= startHour && hour < endHour;
    });
  };

  const weekDays = getWeekDays(currentDate);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  const handleMouseDown = (dayIndex, hour) => {
    if (mode !== "interpreter") return;
    setIsDragging(true);
    setDragStart({ dayIndex, hour });
    setDragEnd({ dayIndex, hour });
  };

  const handleMouseEnter = (dayIndex, hour) => {
    if (!isDragging || mode !== "interpreter") return;
    // Only allow dragging within the same day
    if (dayIndex === dragStart.dayIndex) {
      setDragEnd({ dayIndex, hour });
    }
  };

  const handleMouseUp = () => {
    if (!isDragging || !dragStart || !dragEnd || mode !== "interpreter") {
      setIsDragging(false);
      setDragStart(null);
      setDragEnd(null);
      return;
    }

    const day = weekDays[dragStart.dayIndex];
    const dateStr = formatDate(day);
    const startHour = Math.min(dragStart.hour, dragEnd.hour);
    const endHour = Math.max(dragStart.hour, dragEnd.hour) + 1;

    // Check if there's already a slot in this time range
    const hasConflict = slots.some(slot => {
      if (slot.date !== dateStr) return false;
      const slotStart = parseInt(slot.start_time.split(":")[0]);
      const slotEnd = parseInt(slot.end_time.split(":")[0]);
      return (startHour < slotEnd && endHour > slotStart);
    });

    if (hasConflict) {
      toast.error("Time slot conflicts with existing availability");
    } else if (onCreateSlot) {
      onCreateSlot({
        date: dateStr,
        start_time: formatTime(startHour),
        end_time: formatTime(endHour),
      });
    }

    setIsDragging(false);
    setDragStart(null);
    setDragEnd(null);
  };

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isDragging) {
        handleMouseUp();
      }
    };

    document.addEventListener("mouseup", handleGlobalMouseUp);
    return () => {
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDragging, dragStart, dragEnd]);

  const isCellInDragRange = (dayIndex, hour) => {
    if (!isDragging || !dragStart || !dragEnd) return false;
    if (dayIndex !== dragStart.dayIndex) return false;
    const minHour = Math.min(dragStart.hour, dragEnd.hour);
    const maxHour = Math.max(dragStart.hour, dragEnd.hour);
    return hour >= minHour && hour <= maxHour;
  };

  const today = new Date();
  const todayStr = formatDate(today);

  return (
    <div 
      ref={gridRef}
      className="week-view-container border border-slate-200 rounded-lg overflow-hidden"
      style={{ userSelect: isDragging ? 'none' : 'auto' }}
    >
      {/* Header with day names and dates */}
      <div className="grid grid-cols-8 bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="p-3 text-xs font-semibold text-slate-600 bg-slate-50 border-r border-slate-200">
          Time
        </div>
        {weekDays.map((day, index) => {
          const dateStr = formatDate(day);
          const isToday = dateStr === todayStr;
          return (
            <div
              key={index}
              className={`p-3 text-center border-r border-slate-200 last:border-r-0 ${
                isToday ? "bg-blue-50" : "bg-white"
              }`}
            >
              <div className={`text-sm font-semibold ${
                isToday ? "text-blue-600" : "text-slate-900"
              }`} style={{ fontFamily: 'Manrope' }}>
                {dayNames[day.getDay()]}
              </div>
              <div className={`text-xs mt-1 ${
                isToday ? "text-blue-600" : "text-slate-600"
              }`}>
                {day.getMonth() + 1}/{day.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* Time grid */}
      <div className="week-view-grid" style={{ maxHeight: '600px', overflowY: 'auto' }}>
        {hours.map((hour) => (
          <div key={hour} className="grid grid-cols-8 border-b border-slate-200 last:border-b-0">
            {/* Time label */}
            <div className="p-2 text-xs text-slate-600 bg-slate-50 border-r border-slate-200 flex items-start justify-end">
              {formatTime(hour)}
            </div>
            
            {/* Day cells */}
            {weekDays.map((day, dayIndex) => {
              const dateStr = formatDate(day);
              const daySlots = getSlotsForDateTime(dateStr, hour);
              const isInDragRange = isCellInDragRange(dayIndex, hour);
              const isPast = new Date(dateStr) < new Date(todayStr) || 
                            (dateStr === todayStr && hour < today.getHours());

              return (
                <div
                  key={`${dateStr}-${hour}`}
                  data-testid={`time-slot-${dateStr}-${hour}`}
                  onMouseDown={() => handleMouseDown(dayIndex, hour)}
                  onMouseEnter={() => handleMouseEnter(dayIndex, hour)}
                  className={`time-cell border-r border-slate-200 last:border-r-0 relative ${
                    isPast ? "bg-slate-50 cursor-not-allowed" : ""
                  } ${
                    isInDragRange ? "bg-blue-100 border-2 border-blue-400 border-dashed" : ""
                  } ${
                    mode === "interpreter" && !isPast ? "cursor-crosshair hover:bg-slate-50" : ""
                  }`}
                  style={{ minHeight: '48px' }}
                >
                  {daySlots.map((slot) => (
                    <div
                      key={slot.slot_id}
                      data-testid={`week-slot-${slot.slot_id}`}
                      onClick={() => onSlotClick && (slot.status === "available" || slot.status === "pending") && onSlotClick(slot)}
                      className={`absolute inset-0 p-1 text-xs font-medium ${
                        slot.status === "available"
                          ? "bg-emerald-500/90 text-white cursor-pointer hover:bg-emerald-600"
                          : slot.status === "booked"
                          ? "bg-rose-500/90 text-white"
                          : "bg-amber-500/90 text-white cursor-pointer hover:bg-amber-600"
                      }`}
                      style={{
                        top: 0,
                        height: '100%',
                      }}
                    >
                      <div className="truncate">
                        {slot.start_time} - {slot.end_time}
                      </div>
                      <div className="text-xs capitalize opacity-90">
                        {slot.status === "pending" ? "request pending" : slot.status}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WeekView;